import React, { useContext, PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import CloseIcon from '@mui/icons-material/Close';
import MuiLanguage from '@mui/icons-material/Language';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import { isNotNull } from '@yi/core';
import { i18n_home } from '@yi/i18n/phrases/i18n_home';
import { i18n_language } from '@yi/i18n/phrases/i18n_language';
import { i18n_sale } from '@yi/i18n/phrases/i18n_sale';

import { analytics } from '~/common';
import { useHeaderGlobalSearchButtonVisibility } from '~/src/common/appearence/header';
import { getPageTitleByHrefPathname } from '~/src/common/finder-page';
import { getLocalizedPath, useHomePathname } from '~/src/common/navigation';
import {
  ARTICLES_PATHNAME,
  CHALLENGES_PATHNAME,
  CLASSES_PATHNAME,
  COURSES_PATHNAME,
  LIBRARY_PATHNAME,
  MEDITATIONS_PATHNAME,
  BREATHWORK_PATHNAME,
  PREMIUM_TRAININGS_PATHNAME,
  SERIES_PATHNAME,
  TEACHERS_PATHNAME,
} from '~/src/common/routes';
import { useCurrentUser } from '~/src/common/user';
import { breakpoints, darkGrey, turquoise } from '~/styles';

import { SpecialOffersCoursesQueryEn } from './_SpecialOffersCoursesQueryEn';
import { SpecialOffersCoursesQueryEs } from './_SpecialOffersCoursesQueryEs';
import {
  Drawer,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ItemText,
  LanguageMenuItem,
  Menu,
  MenuContainer,
  MenuFooter,
  MenuItem,
  NewItems,
  StyledLocalizedLink,
} from './elements';
import { SiteWideSearchInput } from './SiteWideSearchInput';
import {
  ArticlesIcon,
  ClassesIcon,
  CoursesIcon,
  HomeIcon,
  IconChallenges,
  IconCheckmarkOrange,
  IconSeries,
  IconPranayama,
  IconPremiumTrainings,
  MeditationsIcon,
  TeachersIcon,
} from '../icons';
import { useLocale } from '../LocaleProvider';
import { PlaylistContext } from '../PlayListPlayer';

interface Props {
  showSideNav: boolean;
  hideDefaultSideNav?: boolean;
  toggleSideNav(event: React.KeyboardEvent | React.MouseEvent): void;
}

const styles = {
  searchInput: css({
    display: 'block',
    [breakpoints.minTabletLandscape]: {
      display: 'none',
    },
  }),
};

export const SideNav: React.FC<PropsWithChildren<Props>> = ({
  showSideNav,
  hideDefaultSideNav,
  toggleSideNav,
}) => {
  const { locale } = useLocale();
  const { isLoggedIn, refetch } = useCurrentUser({ forceRefresh: true });
  const [expanded, setExpanded] = React.useState(false);
  const isEs = locale === 'es';
  const { data } = isEs ? SpecialOffersCoursesQueryEs() : SpecialOffersCoursesQueryEn();
  const { playlistEnabled, showDetailView } = useContext(PlaylistContext);
  const homePathName = useHomePathname();
  const { isShown: isGlobalSearchIconShown } = useHeaderGlobalSearchButtonVisibility();

  return (
    <Drawer
      variant="permanent"
      playlistEnabled={playlistEnabled}
      playlistDetailView={showDetailView}
    >
      <MenuContainer
        role="presentation"
        showSideNav={showSideNav}
        hideDefaultSideNav={hideDefaultSideNav}
      >
        {isGlobalSearchIconShown && <SiteWideSearchInput css={styles.searchInput} />}
        <Menu>
          <StyledLocalizedLink
            onClick={() => analytics.sendLeftNavLinked(homePathName)}
            to={homePathName}
          >
            <MenuItem>
              <HomeIcon style={{ color: '#fff' }} />
              <ItemText fadeRight={showSideNav}>{i18n_home(locale)}</ItemText>
            </MenuItem>
          </StyledLocalizedLink>

          {/* {locale === 'en' && (
              <StyledLocalizedLink onClick={() => analytics.sendLeftNavLinked('/live')} to="/live">
                <MenuItem>
                  <IconLiveSignal fill={'#fff'} />
                  <ItemText
                    style={{
                      fontSize: isEs ? '.5rem' : undefined,
                      paddingTop: isEs ? '3px' : undefined,
                    }}
                    fadeRight={showSideNav}
                  >
                    {i18n_live(locale)}
                    <StyledLiveChip
                      style={{
                        fontSize: isEs ? '.5rem' : undefined,
                        padding: isEs ? '3px 5px' : undefined,
                        bottom: isEs ? '0px' : undefined,
                      }}
                      as="span"
                    >
                      {i18n_live(locale)}
                    </StyledLiveChip>
                  </ItemText>
                </MenuItem>
              </StyledLocalizedLink>
            )} */}
          <StyledLocalizedLink
            onClick={() => analytics.sendLeftNavLinked(CLASSES_PATHNAME)}
            to={CLASSES_PATHNAME}
          >
            <MenuItem>
              <ClassesIcon style={{ color: '#fff' }} />
              <ItemText fadeRight={showSideNav}>
                {getPageTitleByHrefPathname(CLASSES_PATHNAME, locale)}
              </ItemText>
            </MenuItem>
          </StyledLocalizedLink>
          <StyledLocalizedLink
            onClick={() => analytics.sendLeftNavLinked(MEDITATIONS_PATHNAME)}
            to={MEDITATIONS_PATHNAME}
          >
            <MenuItem>
              <MeditationsIcon style={{ color: '#fff' }} />
              <ItemText fadeRight={showSideNav}>
                {getPageTitleByHrefPathname(MEDITATIONS_PATHNAME, locale)}
              </ItemText>
            </MenuItem>
          </StyledLocalizedLink>
          {locale === 'en' && (
            <StyledLocalizedLink
              onClick={() => analytics.sendLeftNavLinked(BREATHWORK_PATHNAME)}
              to={BREATHWORK_PATHNAME}
            >
              <MenuItem>
                <IconPranayama
                  style={{
                    width: '24px',
                    height: '24px',
                    color: '#fff',
                  }}
                />
                <ItemText fadeRight={showSideNav}>
                  {getPageTitleByHrefPathname(BREATHWORK_PATHNAME, locale)}
                </ItemText>
              </MenuItem>
            </StyledLocalizedLink>
          )}
          <StyledLocalizedLink
            onClick={() => analytics.sendLeftNavLinked(SERIES_PATHNAME)}
            to={SERIES_PATHNAME}
          >
            <MenuItem>
              <IconSeries
                style={{
                  width: '24px',
                  height: '24px',
                  color: '#fff',
                }}
              />
              <ItemText fadeRight={showSideNav}>
                {getPageTitleByHrefPathname(SERIES_PATHNAME, locale)}
              </ItemText>
            </MenuItem>
          </StyledLocalizedLink>
          <StyledLocalizedLink
            onClick={() => analytics.sendLeftNavLinked(ARTICLES_PATHNAME)}
            data-testid="side-nav-articles-button"
            to={ARTICLES_PATHNAME}
          >
            <MenuItem>
              <ArticlesIcon style={{ color: '#fff' }} />
              <ItemText fadeRight={showSideNav}>
                {getPageTitleByHrefPathname(ARTICLES_PATHNAME, locale)}
              </ItemText>
            </MenuItem>
          </StyledLocalizedLink>
          <StyledLocalizedLink
            onClick={() => analytics.sendLeftNavLinked(COURSES_PATHNAME)}
            to={COURSES_PATHNAME}
          >
            <MenuItem>
              <CoursesIcon style={{ color: '#fff' }} />
              <ItemText fadeRight={showSideNav}>
                {getPageTitleByHrefPathname(COURSES_PATHNAME, locale)}
              </ItemText>
              {showSideNav &&
              data &&
              data.SiteVars.featured_ecourses &&
              data.SiteVars.featured_ecourses.filter(isNotNull).length ? (
                <NewItems>{i18n_sale(locale)}</NewItems>
              ) : null}
            </MenuItem>
          </StyledLocalizedLink>
          {locale === 'en' && (
            <StyledLocalizedLink
              onClick={() => analytics.sendLeftNavLinked(CHALLENGES_PATHNAME)}
              to={CHALLENGES_PATHNAME}
            >
              <MenuItem>
                <IconChallenges
                  style={{
                    width: '24px',
                    height: '24px',
                    color: '#fff',
                  }}
                />
                <ItemText fadeRight={showSideNav}>
                  {getPageTitleByHrefPathname(CHALLENGES_PATHNAME, locale)}
                </ItemText>
              </MenuItem>
            </StyledLocalizedLink>
          )}
          <StyledLocalizedLink
            onClick={() => analytics.sendLeftNavLinked(TEACHERS_PATHNAME)}
            to={TEACHERS_PATHNAME}
          >
            <MenuItem>
              <TeachersIcon style={{ color: '#fff' }} />
              <ItemText fadeRight={showSideNav}>
                {getPageTitleByHrefPathname(TEACHERS_PATHNAME, locale)}
              </ItemText>
            </MenuItem>
          </StyledLocalizedLink>
          {isLoggedIn && (
            <StyledLocalizedLink
              onClick={() => analytics.sendLeftNavLinked(LIBRARY_PATHNAME)}
              to={LIBRARY_PATHNAME}
              data-testid="side-nav-library-button"
            >
              <MenuItem>
                <BookmarksOutlinedIcon style={{ color: '#fff' }} />
                <ItemText fadeRight={showSideNav}>
                  {getPageTitleByHrefPathname(LIBRARY_PATHNAME, locale)}
                </ItemText>
              </MenuItem>
            </StyledLocalizedLink>
          )}
          <MenuFooter>
            <Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
              <AccordionSummary aria-controls="languagePanelbh-content" id="languagePanelbh-header">
                <MenuItem>
                  <MuiLanguage style={{ color: '#fff' }} />
                  <ItemText fadeRight={showSideNav}>{i18n_language(locale)}</ItemText>
                </MenuItem>
              </AccordionSummary>
              <AccordionDetails>
                <a onClick={() => refetch()} href={getLocalizedPath(homePathName, 'en')}>
                  <LanguageMenuItem showSideNav={showSideNav}>
                    {locale === 'en' ? (
                      <IconCheckmarkOrange style={{ width: 25, height: 25 }} />
                    ) : (
                      <div style={{ width: 25, height: 25 }} />
                    )}
                    <ItemText fadeRight={showSideNav}>English</ItemText>
                  </LanguageMenuItem>
                </a>
                <a onClick={() => refetch()} href={getLocalizedPath(homePathName, 'es')}>
                  <LanguageMenuItem showSideNav={showSideNav}>
                    {isEs ? (
                      <IconCheckmarkOrange style={{ width: 25, height: 25 }} />
                    ) : (
                      <div style={{ width: 25, height: 25 }} />
                    )}
                    <ItemText fadeRight={showSideNav}>Español</ItemText>
                  </LanguageMenuItem>
                </a>
              </AccordionDetails>
            </Accordion>

            <StyledLocalizedLink
              onClick={() => analytics.sendLeftNavLinked(PREMIUM_TRAININGS_PATHNAME)}
              to={PREMIUM_TRAININGS_PATHNAME}
            >
              <MenuItem>
                <IconPremiumTrainings
                  css={{
                    width: '24px',
                    height: '24px',
                    color: '#fff',
                  }}
                />
                <ItemText fadeRight={showSideNav}>
                  {getPageTitleByHrefPathname(PREMIUM_TRAININGS_PATHNAME, locale)}
                </ItemText>
              </MenuItem>
            </StyledLocalizedLink>
          </MenuFooter>
        </Menu>
        <Box display={{ sm: 'none' }} textAlign="center" color={darkGrey}>
          <Fab
            size="small"
            color="inherit"
            aria-label="close"
            style={{ background: turquoise }}
            onClick={toggleSideNav}
          >
            <CloseIcon />
          </Fab>
        </Box>
      </MenuContainer>
    </Drawer>
  );
};
